import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NzTreeNode } from 'src/app/modules/shared/models/tree-node.model';
import { FormFlowInfo } from '../../models/form/form-flow-info.model';
import { UserInfo } from '../../models/form/user-info.model';
import { OrganizationType } from '../../models/staff/organization-type.enum';
import { User } from '../../models/staff/user.model';
import { GlobalService } from '../../providers/global.service';

// ====================================================================
// 服务端数据契约常量 (Backend Data Contracts)
// 以下常量值由服务端定义，前端必须严格遵守，不可修改其值。
// ====================================================================

/** 特殊部门ID —— 服务端用负数 departmentId 表示虚拟职能 */
const SPECIAL_DEPT = {
  SEAL_HOLDER: -98, // 印章持有人（持章人）
  SUPERVISION: -96, // 专监
  RELATED_SUPERVISION: -95, // 对口专监
} as const;

/** 特殊节点标识 */
const SPECIAL_NODE = {
  DEFAULT_SECTION_ID: -1, // 默认标段ID（无标段）
  EMPTY_LEAF: '-', // 空叶子补位节点（无可选人员时的占位）
  FILE_RECEIVER_LABEL: '文件签收人', // 文件签收岗位名称
  PERFORMANCE_KEYWORD: '履约', // 履约类岗位关键字
} as const;

/** 用户展示标签后缀 */
const ROLE_SUFFIX = {
  PERFORMANCE: '[履约]',
  RELATED_SUPERVISION: '[对口专监]',
  SUPERVISION: '[专监]',
  SEAL_HOLDER: '[持章人]',
} as const;

/** 级联路径分隔符 */
const PATH_SEPARATOR = '#';

@Component({
  selector: 'user-select-popover',
  templateUrl: './user-select-popover.component.html',
  styleUrls: ['./user-select-popover.component.scss'],
})
export class UserSelectPopoverComponent {
  @Input() required = false;

  @Input() disabled = false;

  @Input() editable = true;

  showTime = false;

  @Input() hideRetention = false;

  @ViewChild('myCascader') myCascader!: TemplateRef<any>;
  currentUser: User = new User();

  loginUser: User = this.globalService.userInfo;

  cascaderValue: string[] = [];

  autoFocus: boolean = true;

  userMap: { [key: string]: User } = {};

  init = true;

  @Input()
  set userList(value: User[]) {
    this._userList = value;
    this.userMap = {};
    for (const user of value) {
      this.userMap[this.buildUserMapKey(user.postId, user.id, user.sectionId)] =
        user;
    }
  }

  get userList(): User[] {
    return this._userList;
  }

  _userList: User[] = [];

  @Output()
  userInfoChange: EventEmitter<UserInfo> = new EventEmitter<UserInfo>();

  @Input()
  set userInfo(value: UserInfo) {
    this._user = value;
  }

  get userInfo(): UserInfo {
    return this._user;
  }

  _user: UserInfo = new UserInfo();

  @Input()
  set flow(value: FormFlowInfo) {
    this._flow = value;
  }

  get flow(): FormFlowInfo {
    return this._flow;
  }

  _flow: FormFlowInfo = new FormFlowInfo();

  @Input()
  set archiveTreeNode(value: NzTreeNode[]) {
    this._archiveTreeNode = value;
    this.prepare();
  }

  get archiveTreeNode(): NzTreeNode[] {
    return this._archiveTreeNode;
  }

  _archiveTreeNode: NzTreeNode[] = [];
  postTreeNodeFull: NzTreeNode[] = [];
  /** 扁平缓存：树节点 value → 节点对象 + 父链标签信息 */
  private nodeMap: Map<
    string,
    {
      node: NzTreeNode;
      sectionLabel?: string;
      orgLabel?: string;
      departLabel?: string;
    }
  > = new Map();

  constructor(private globalService: GlobalService) {
    this.userInfo.auto = false;
  }

  ngOnInit(): void {}

  // ====================================================================
  // Popover 展开交互 (DOM Hack — 已知的技术债务，保持原样)
  // ====================================================================

  visibleChange(visible: boolean) {
    if (visible) {
      setTimeout(() => {
        this.myCascader.elementRef.nativeElement.click();
        setTimeout(() => {
          const elemenets = document.getElementsByClassName(
            'cdk-overlay-connected-position-bounding-box',
          );
          if (elemenets && elemenets.length > 0) {
            const element = elemenets[elemenets.length - 1];
            const childs = element.getElementsByClassName(
              'ant-cascader-menu-item-active',
            );
            if (childs && childs.length > 0 && childs.length < 5) {
              (childs[childs.length - 1] as any).click();
            }
          }
        }, 100);
      }, 100);
    }
  }

  // ====================================================================
  // 工具方法 (Utility Methods)
  // ====================================================================

  /**
   * 构建 userMap 的复合键
   * 格式: `{postId}#{userId}@{sectionId}`
   */
  private buildUserMapKey(postId: any, userId: any, sectionId?: any): string {
    return `${postId}${PATH_SEPARATOR}${userId}@${sectionId ?? SPECIAL_NODE.DEFAULT_SECTION_ID}`;
  }

  /**
   * 构建级联选择器的完整路径数组 (5 层: section → org → dept → post → user)
   * 从 UserInfo 对象生成
   */
  private buildCascaderValueFromUserInfo(info: UserInfo): string[] {
    const s = info.sectionId ?? SPECIAL_NODE.DEFAULT_SECTION_ID;
    const t = info.organizationType;
    const o = info.organizationId;
    const d = info.departmentId;
    const p = info.postId;
    const u = info.userId;
    return [
      `${s}${PATH_SEPARATOR}${t}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}${PATH_SEPARATOR}${d}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}${PATH_SEPARATOR}${d}${PATH_SEPARATOR}${p}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}${PATH_SEPARATOR}${d}${PATH_SEPARATOR}${p}${PATH_SEPARATOR}${u}`,
    ];
  }

  /**
   * 从 User (currentUser) 实体构建级联路径
   */
  private buildCascaderValueFromUser(user: User): string[] {
    const s = user.sectionId ?? SPECIAL_NODE.DEFAULT_SECTION_ID;
    const t = user.organizationType;
    const o = user.organizationId;
    const d = user.departmentId;
    const p = user.postId;
    const u = user.id;
    return [
      `${s}${PATH_SEPARATOR}${t}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}${PATH_SEPARATOR}${d}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}${PATH_SEPARATOR}${d}${PATH_SEPARATOR}${p}`,
      `${s}${PATH_SEPARATOR}${t}${PATH_SEPARATOR}${o}${PATH_SEPARATOR}${d}${PATH_SEPARATOR}${p}${PATH_SEPARATOR}${u}`,
    ];
  }

  /**
   * 组装用户名称标签 (nameLabel)
   * 格式: "张三[履约][对口专监][专监][持章人]"
   */
  private buildNameLabel(info: UserInfo): string {
    return (
      (info.userName ?? '') +
      (info.performance ? ROLE_SUFFIX.PERFORMANCE : '') +
      (info.relatedSupervision ? ROLE_SUFFIX.RELATED_SUPERVISION : '') +
      (info.supervision ? ROLE_SUFFIX.SUPERVISION : '') +
      (info.sealHolder ? ROLE_SUFFIX.SEAL_HOLDER : '')
    );
  }

  /**
   * 组装组织路径标签 (orgLabel)
   * 格式: "单位简称\部门名\岗位名[履约]"
   */
  private buildOrgLabel(info: UserInfo): string {
    const orgName = info.organizationShortName
      ? info.organizationShortName
      : info.organizationName!;
    const deptPart = info.departmentName ? '\\' + info.departmentName : '';
    const postPart = info.postName
      ? '\\' +
        (info.postPerformance
          ? info.postName + ROLE_SUFFIX.PERFORMANCE
          : info.postName)
      : '';
    return orgName + deptPart + postPart;
  }

  /**
   * 组装不含用户名的名称后缀标签（用于 onChanges 中尚未获取完整 User 信息的场景）
   */
  private buildRoleSuffixLabel(info: UserInfo): string {
    return (
      (info.performance ? ROLE_SUFFIX.PERFORMANCE : '') +
      (info.relatedSupervision ? ROLE_SUFFIX.RELATED_SUPERVISION : '') +
      (info.supervision ? ROLE_SUFFIX.SUPERVISION : '') +
      (info.sealHolder ? ROLE_SUFFIX.SEAL_HOLDER : '')
    );
  }

  /**
   * 从级联节点的 value 字符串中解析出最后一段 ID
   */
  private extractLastId(value: string): string {
    const ids = value.split(PATH_SEPARATOR);
    return ids[ids.length - 1];
  }

  /**
   * 将部分组织信息写入 userInfo 并设置 orgLabel（无用户选定时的部分回填）
   */
  private applyPartialOrgInfo(
    orgId: any,
    orgLabel?: string,
    departId?: any,
    departLabel?: string,
    postId?: any,
    postLabel?: string,
  ): void {
    this.userInfo.organizationId = orgId;
    this.userInfo.organizationName = orgLabel ?? '';
    this.userInfo.organizationShortName = orgLabel ?? '';
    if (departId !== undefined) {
      this.userInfo.departmentId = departId;
      this.userInfo.departmentName = departLabel;
    }
    if (postId !== undefined) {
      this.userInfo.postId = postId;
      this.userInfo.postName = postLabel;
    }
    this.userInfo.orgLabel = this.buildOrgLabel(this.userInfo);
  }

  // ====================================================================
  // 核心逻辑 (Core Logic)
  // ====================================================================

  /**
   * 主入口：当 archiveTreeNode 或 userList 变化时触发
   * 1) 按 flow 配置过滤组织树 → postTreeNodeFull
   * 2) 若已有 userId → 回显已选用户
   * 3) 若无 userId → 自动匹配默认人选
   */
  private prepare() {
    if (this._archiveTreeNode.length === 0 || this._userList.length === 0) {
      return;
    }
    console.log('prepare');

    // ---- 步骤1: 按 flow 配置过滤组织树 ----
    this.buildFilteredTree();

    // 其他数据都准备好了。这里需要根据流程信息去匹配对应的岗位、部门、单位
    // 根据流程信息去匹配对应的角色
    // 逻辑如下：
    // 1. 如果存在UserInfo，则根据UserInfo去匹配对应的岗位、部门、单位
    // 2. 如果不存在UserInfo，判断存是否在OrgId，如果存在，仅从该OrgId下的人员中选择，否则从单位类型为OrgType的人员中选择

    // 追加特殊处理：2025-08-26
    // 如果是新建的表单（编辑状态且表单ID不存在），删除人员之后需要按照流程中配置的单位类型/岗位信息重新匹配。
    // 如果是编辑状态的表单（编辑状态且表单ID存在），签字稿（待签字/待盖章）修改流程时，删除人员之后不需要再次匹配

    if (!this.postTreeNodeFull || this.postTreeNodeFull.length === 0) {
      this.userInfo.orgLabel = '没找到单位';
      this.cascaderValue = [];
      return;
    }

    // ---- 步骤2: 回显或自动匹配 ----
    if (this.userInfo && this.userInfo.userId) {
      this.restoreSelectedUser();
    } else {
      this.autoMatchUser();
    }
  }

  /**
   * 按 flow 中的 organizationType / organizationId 过滤组织树
   */
  private buildFilteredTree(): void {
    if (!this._flow.organizationType) {
      this.postTreeNodeFull = this.archiveTreeNode;
    } else {
      const node: NzTreeNode[] = [];
      for (const section of this.archiveTreeNode) {
        if (!section.value.includes(this._flow.organizationType)) {
          continue;
        }
        if (!section.children || section.children.length === 0) {
          continue;
        }
        if (this._flow.organizationId) {
          const sNode: NzTreeNode = {
            label: section.label,
            value: section.value,
            children: [],
            isLeaf: false,
          };
          for (const org of section.children) {
            if (org.value.includes(this._flow.organizationId)) {
              sNode.children!.push(org);
            }
          }
          if (sNode.children && sNode.children.length > 0) {
            node.push(sNode);
          }
        } else {
          node.push(section);
        }
      }
      this.postTreeNodeFull = node;
    }

    // 构建 nodeMap 缓存，供 onChanges 使用 O(1) 查找
    this.nodeMap.clear();
    for (const section of this.postTreeNodeFull) {
      this.nodeMap.set(section.value, { node: section });
      if (!section.children) continue;
      for (const org of section.children) {
        this.nodeMap.set(org.value, { node: org, sectionLabel: section.label });
        if (!org.children) continue;
        for (const depart of org.children) {
          this.nodeMap.set(depart.value, {
            node: depart,
            sectionLabel: section.label,
            orgLabel: org.label,
          });
          if (!depart.children) continue;
          for (const post of depart.children) {
            this.nodeMap.set(post.value, {
              node: post,
              sectionLabel: section.label,
              orgLabel: org.label,
              departLabel: depart.label,
            });
            // 叶子节点(用户) — 如果 post 有 children 且非空叶
            if (post.children) {
              for (const user of post.children) {
                this.nodeMap.set(user.value, {
                  node: user,
                  sectionLabel: section.label,
                  orgLabel: org.label,
                  departLabel: depart.label,
                });
              }
            }
          }
        }
      }
    }
  }

  /**
   * 已有 userId 时的回显逻辑：
   * - 组装 nameLabel / orgLabel
   * - 计算滞留时间
   * - 设置 cascaderValue
   */
  private restoreSelectedUser(): void {
    if (!!this.userInfo.departmentName) {
      this.userInfo.nameLabel = this.buildNameLabel(this.userInfo);
      this.userInfo.orgLabel = this.buildOrgLabel(this.userInfo);
    } else {
      const user =
        this.userMap[
          this.buildUserMapKey(
            this.userInfo.postId,
            this.userInfo.userId,
            this.userInfo.sectionId,
          )
        ];
      if (user && user.id) {
        this.chooseUser(user, user.departmentId);
      } else {
        console.log('用户列表中暂无此人信息');
      }
    }

    // 计算滞留时间
    if (!this.userInfo.operateTime && this.userInfo.createTime!) {
      const currentTime = Date.now();
      const hours =
        (currentTime - this.userInfo.createTime!) / (1000 * 60 * 60);
      const day = Math.floor(hours / 24);
      if (day > 0) {
        const hour = hours % 24;
        this.userInfo.retention =
          '滞留 ' + day + '天' + hour.toFixed(0) + '小时';
      } else {
        const hour = Math.floor(hours);
        const minutes = ((hours - hour) * 60).toFixed(0);
        this.userInfo.retention = '滞留 ' + hour + '小时' + minutes + '分';
      }
    }
    if (this.hideRetention) {
      this.userInfo.retention = '';
    }

    this.cascaderValue = this.buildCascaderValueFromUserInfo(this.userInfo);
  }

  /**
   * 无 userId 时的自动匹配逻辑分发
   */
  private autoMatchUser(): void {
    if (this.userInfo.auto) {
      if (!this.userInfo.userId) {
        this.cascaderValue = [];
      }
      return;
    }

    if (this.postTreeNodeFull.length > 1) {
      // 多个 section 节点 → 无法自动匹配
      this.cascaderValue = [];
      return;
    }

    const sectionNode = this.postTreeNodeFull[0];

    if (sectionNode.children!.length === 1) {
      // 唯一组织 → 尝试精确匹配
      this.autoMatchSingleOrg(sectionNode);
    } else {
      // 多个组织 → 按登录人组织匹配
      this.autoMatchMultiOrg();
    }
  }

  /**
   * 唯一组织下的自动匹配（原 prepare 中 postTreeNodeFull[0].children.length === 1 分支）
   */
  private autoMatchSingleOrg(sectionNode: NzTreeNode): void {
    const cascaderValue: string[] = [];
    let find = false;

    cascaderValue.push(sectionNode.value);
    const org = sectionNode.children![0];
    const orgId = this.extractLastId(org.value);
    cascaderValue.push(org.value);

    for (const depart of org.children!) {
      if (find) break;
      const departId = this.extractLastId(depart.value);

      if (+this.flow.organizationId!) {
        // ---- 有 organizationId: 寻找文件签收人 ----
        for (const post of depart.children!) {
          const postId = this.extractLastId(post.value);
          if (post.label === SPECIAL_NODE.FILE_RECEIVER_LABEL) {
            if (
              post.children!.length === 1 &&
              post.children![0].value !== SPECIAL_NODE.EMPTY_LEAF
            ) {
              // 唯一签收人 → 自动选中
              const user = post.children![0];
              const ids = user.value.split(PATH_SEPARATOR);
              const userId = ids[ids.length - 1];
              this.chooseUser(
                this.userMap[this.buildUserMapKey(postId, userId, ids[0])],
                departId as any as number,
              );
              this.cascaderValue = this.buildCascaderValueFromUserInfo(
                this.userInfo,
              );
              this.userInfoChange.emit(this.userInfo);
            } else {
              // 多个签收人或空 → 展开到岗位级
              cascaderValue.push(depart.value);
              cascaderValue.push(post.value);
              this.cascaderValue = cascaderValue;
              this.applyPartialOrgInfo(
                orgId,
                org.label,
                departId,
                depart.label,
                postId,
                post.label,
              );
            }
            find = true;
          }
        }
      } else {
        // ---- 无 organizationId: 按 departmentId 匹配 ----
        if (+departId === +this.flow.departmentId!) {
          cascaderValue.push(depart.value);
          if (+this.flow.departmentId! === SPECIAL_DEPT.SEAL_HOLDER) {
            // 持章人部门
            this.handleSealHolderDept(
              depart,
              cascaderValue,
              orgId,
              org.label!,
              departId,
            );
            find = true;
          } else {
            if (depart.children! && depart.children!.length > 0) {
              if (+this.flow.postId! > 0) {
                // 有指定岗位
                for (const post of depart.children!) {
                  if (find) break;
                  const postId = this.extractLastId(post.value);
                  if (+postId === +this.flow.postId!) {
                    if (
                      post.children!.length === 1 &&
                      post.children![0].value !== SPECIAL_NODE.EMPTY_LEAF
                    ) {
                      const user = post.children![0];
                      const ids = user.value.split(PATH_SEPARATOR);
                      const userId = ids[ids.length - 1];
                      this.chooseUser(
                        this.userMap[
                          this.buildUserMapKey(postId, userId, ids[0])
                        ],
                        departId as any as number,
                      );
                      this.cascaderValue = this.buildCascaderValueFromUserInfo(
                        this.userInfo,
                      );
                      this.userInfoChange.emit(this.userInfo);
                    } else {
                      cascaderValue.push(post.value);
                      this.cascaderValue = cascaderValue;
                      this.applyPartialOrgInfo(
                        orgId,
                        org.label,
                        departId,
                        depart.label,
                        postId,
                        post.label,
                      );
                    }
                    find = true;
                  }
                }
              } else {
                // 无指定岗位 → 展开到部门级
                this.cascaderValue = cascaderValue;
                this.applyPartialOrgInfo(
                  orgId,
                  org.label,
                  departId,
                  depart.label,
                );
                find = true;
              }
            }
          }
        }
      }
    }

    if (!find) {
      this.cascaderValue = cascaderValue;
      this.applyPartialOrgInfo(orgId, org.label);
    }
  }

  /**
   * 处理持章人（-98）部门的自动选人逻辑
   */
  private handleSealHolderDept(
    depart: NzTreeNode,
    cascaderValue: string[],
    orgId: any,
    orgLabel: string,
    departId: any,
  ): void {
    if (
      depart.children! &&
      depart.children!.length === 1 &&
      depart.children[0].value !== SPECIAL_NODE.EMPTY_LEAF
    ) {
      const user = depart.children![0];
      const ids = user.value.split(PATH_SEPARATOR);
      const postId = ids[ids.length - 2];
      const userId = ids[ids.length - 1];
      this.chooseUser(
        this.userMap[this.buildUserMapKey(postId, userId, ids[0])],
        departId,
      );
      this.cascaderValue = this.buildCascaderValueFromUserInfo(this.userInfo);
      this.userInfoChange.emit(this.userInfo);
    } else {
      this.cascaderValue = cascaderValue;
      this.applyPartialOrgInfo(orgId, orgLabel, departId, depart.label);
    }
  }

  /**
   * 多个组织时的自动匹配（原 prepare 中 else 分支 + normalHandle / lyHandle）
   */
  private autoMatchMultiOrg(): void {
    if (this.loginUser.organizationType !== this.flow.organizationType) {
      this.cascaderValue = [];
      return;
    }

    // 检测是否为履约岗位
    let isLy = false;
    let findPost = false;
    for (const org of this.postTreeNodeFull[0].children!) {
      for (const depart of org.children!) {
        if (findPost) break;
        for (const post of depart.children!) {
          if (findPost) break;
          const postId = this.extractLastId(post.value);
          if (+postId === +this.flow.postId!) {
            findPost = true;
            if (post.label!.indexOf(SPECIAL_NODE.PERFORMANCE_KEYWORD) > 0) {
              isLy = true;
            }
            break;
          }
        }
      }
    }

    if (isLy) {
      this.handlePerformanceMatch();
    } else {
      this.normalHandle();
    }
  }

  /**
   * 履约岗位的匹配逻辑（原 prepare 中 isLy 分支 + lyHandle）
   */
  private handlePerformanceMatch(): void {
    const hasLyOrgs: Array<{ cas: NzTreeNode[]; isLogin: boolean }> = [];
    const section = this.postTreeNodeFull[0];
    for (const org of this.postTreeNodeFull[0].children!) {
      let isLogin = false;
      const orgId = this.extractLastId(org.value);
      if (+orgId === +this.loginUser.organizationId!) {
        isLogin = true;
      }
      let find = false;
      for (const depart of org.children!) {
        if (find) break;
        for (const post of depart.children!) {
          if (find) break;
          const postId = this.extractLastId(post.value);
          if (+postId === +this.flow.postId!) {
            if (post.children![0].value !== SPECIAL_NODE.EMPTY_LEAF) {
              hasLyOrgs.push({
                cas: [section, org, depart, post],
                isLogin,
              });
              find = true;
              break;
            }
          }
        }
      }
    }

    switch (hasLyOrgs.length) {
      case 1:
        this.lyHandle(hasLyOrgs[0]);
        break;
      default:
        let find = false;
        for (const item of hasLyOrgs) {
          if (find) break;
          if (item.isLogin) {
            find = true;
            this.lyHandle(item);
            break;
          }
        }
        if (!find) {
          this.cascaderValue = [];
        }
        break;
    }
  }

  // ====================================================================
  // normalHandle / lyHandle（保留原有结构，替换字面量为常量）
  // ====================================================================

  normalHandle() {
    let find = false;
    const cascaderValue: string[] = [];
    for (const org of this.postTreeNodeFull) {
      if (find) break;
      const orgId = this.extractLastId(org.value);
      if (+orgId === +this.loginUser.organizationId!) {
        for (const depart of org.children!) {
          if (find) break;
          const departId = this.extractLastId(depart.value);
          if (+departId === +this.flow.departmentId!) {
            cascaderValue.push(org.value);
            cascaderValue.push(depart.value);
            if (+this.flow.departmentId! === SPECIAL_DEPT.SEAL_HOLDER) {
              if (
                depart.children! &&
                depart.children!.length === 1 &&
                depart.children[0].value !== SPECIAL_NODE.EMPTY_LEAF
              ) {
                const user = depart.children![0];
                const ids = user.value.split(PATH_SEPARATOR);
                const postId = ids[ids.length - 2];
                const userId = ids[ids.length - 1];
                this.chooseUser(
                  this.userMap[this.buildUserMapKey(postId, userId, ids[0])],
                  +departId,
                );
                this.cascaderValue = this.buildCascaderValueFromUserInfo(
                  this.userInfo,
                );
                this.userInfoChange.emit(this.userInfo);
              } else {
                this.cascaderValue = cascaderValue;
                this.applyPartialOrgInfo(
                  orgId,
                  org.label,
                  departId,
                  depart.label,
                );
              }
              find = true;
            } else {
              if (depart.children! && depart.children!.length > 0) {
                if (+this.flow.postId! > 0) {
                  for (const post of depart.children!) {
                    if (find) break;
                    const postId = this.extractLastId(post.value);

                    if (+postId === +this.flow.postId!) {
                      if (
                        post.children!.length === 1 &&
                        !post.children![0].disabled
                      ) {
                        const user = post.children![0];
                        const ids = user.value.split(PATH_SEPARATOR);
                        const userId = ids[ids.length - 1];
                        this.chooseUser(
                          this.userMap[
                            this.buildUserMapKey(postId, userId, ids[0])
                          ],
                          +departId,
                        );
                        this.cascaderValue =
                          this.buildCascaderValueFromUserInfo(this.userInfo);
                        this.userInfoChange.emit(this.userInfo);
                      } else {
                        cascaderValue.push(post.value);
                        this.cascaderValue = cascaderValue;
                        this.applyPartialOrgInfo(
                          orgId,
                          org.label,
                          departId,
                          depart.label,
                          postId,
                          post.label,
                        );
                      }

                      find = true;
                    }
                  }
                } else {
                  this.cascaderValue = cascaderValue;
                  this.applyPartialOrgInfo(
                    orgId,
                    org.label,
                    departId,
                    depart.label,
                  );
                }
                find = true;
              }
            }
          }
        }
      }
    }
  }

  lyHandle(item: any) {
    const cascaderValue: string[] = [];
    const section = item.cas[0];
    const org = item.cas[1];
    const depart = item.cas[2];
    const post = item.cas[3];

    if (
      post.children!.length === 1 &&
      post.children![0].value !== SPECIAL_NODE.EMPTY_LEAF
    ) {
      const user = post.children![0];
      const [sectionId, orgType, orgId, departId, postId, userId] =
        user.value.split(PATH_SEPARATOR);
      this.chooseUser(
        this.userMap[this.buildUserMapKey(postId, userId, sectionId)],
        departId,
      );
      this.cascaderValue = this.buildCascaderValueFromUserInfo(this.userInfo);
      this.userInfoChange.emit(this.userInfo);
    } else {
      cascaderValue.push(section.value);
      cascaderValue.push(org.value);
      cascaderValue.push(depart.value);
      cascaderValue.push(post.value);
      const [sectionId, orgType, orgId, departId, postId] =
        post.value.split(PATH_SEPARATOR);
      this.cascaderValue = cascaderValue;
      this.userInfo.sectionId = sectionId;
      this.applyPartialOrgInfo(
        orgId,
        org.label,
        departId,
        depart.label,
        postId,
        post.label,
      );
    }
  }

  // ====================================================================
  // 选人 & 级联变更
  // ====================================================================

  chooseUser(user: User, departId?: number) {
    if (!user) return;
    this.currentUser = user;
    const userInfo = new UserInfo();
    userInfo.userId = this.currentUser.id;
    userInfo.userName = this.currentUser.name;
    userInfo.organizationType = this.currentUser.organizationType;
    userInfo.organizationId = this.currentUser.organizationId;
    userInfo.organizationName = this.currentUser.organizationName;
    userInfo.organizationShortName = this.currentUser.organizationShortName;
    userInfo.departmentId = this.currentUser.departmentId;
    userInfo.departmentName = this.currentUser.departmentName;
    userInfo.postId = this.currentUser.postId;
    userInfo.postName = this.currentUser.postName;
    userInfo.performance = this.currentUser.performance;
    userInfo.postPerformance = this.currentUser.postPerformance;
    userInfo.sectionId =
      this.currentUser.sectionId ?? SPECIAL_NODE.DEFAULT_SECTION_ID;
    // 根据服务端契约的特殊部门ID判定虚拟角色身份
    userInfo.sealHolder = departId === SPECIAL_DEPT.SEAL_HOLDER;
    userInfo.supervision = departId === SPECIAL_DEPT.SUPERVISION;
    userInfo.relatedSupervision = departId === SPECIAL_DEPT.RELATED_SUPERVISION;
    userInfo.nameLabel = this.buildNameLabel(userInfo);
    userInfo.orgLabel = this.buildOrgLabel(userInfo);
    this.userInfo = userInfo;
  }

  onChanges(values: string[]): void {
    console.log('onUserChanges', values);
    if (values.length === 0) {
      const userInfo = new UserInfo();
      userInfo.auto = true;
      this.userInfoChange.emit(userInfo);
      return;
    }
    const userValue = values[values.length - 1];
    if (!userValue) return;

    const [sectionId, orgType, orgId, departId, postId, userId] =
      userValue.split(PATH_SEPARATOR);

    const userInfo = new UserInfo();
    userInfo.nameLabel = '请选择';

    // ---- 利用 nodeMap 逐级查找节点标签 ----
    const sectionKey = `${sectionId}${PATH_SEPARATOR}${orgType}`;
    const sectionEntry = this.nodeMap.get(sectionKey);
    if (!sectionEntry) return;

    userInfo.sectionId = +sectionId;
    userInfo.organizationType = orgType as OrganizationType;

    if (!orgId) {
      // 仅选到 section 级
      this.userInfo = userInfo;
      return;
    }

    const orgKey = `${sectionKey}${PATH_SEPARATOR}${orgId}`;
    const orgEntry = this.nodeMap.get(orgKey);
    if (!orgEntry) return;

    userInfo.organizationId = +orgId;
    userInfo.organizationName = orgEntry.node.label;
    userInfo.organizationShortName = orgEntry.node.label;

    if (!departId) {
      userInfo.orgLabel = orgEntry.node.label;
      userInfo.nameLabel = '请选择';
      this.userInfo = userInfo;
      return;
    }

    const departKey = `${orgKey}${PATH_SEPARATOR}${departId}`;
    const departEntry = this.nodeMap.get(departKey);
    if (!departEntry) return;

    userInfo.departmentId = +departId;
    userInfo.departmentName = departEntry.node.label;
    userInfo.orgLabel = orgEntry.node.label + '\\' + departEntry.node.label;

    if (!postId) {
      userInfo.nameLabel = '请选择';
      this.userInfo = userInfo;
      return;
    }

    // 岗位级：尝试两种 key 格式匹配（可能带或不带 userId）
    const postKey = `${departKey}${PATH_SEPARATOR}${postId}`;
    const postKeyWithUser = userId
      ? `${postKey}${PATH_SEPARATOR}${userId}`
      : '';
    const postEntry =
      this.nodeMap.get(postKey) ||
      (postKeyWithUser ? this.nodeMap.get(postKeyWithUser) : undefined);

    if (!postEntry) return;

    const postNode = postEntry.node;
    userInfo.postId = +postId;
    userInfo.postName = postNode.label;
    userInfo.nameLabel = this.buildRoleSuffixLabel(userInfo);
    userInfo.orgLabel =
      orgEntry.node.label +
      '\\' +
      departEntry.node.label +
      '\\' +
      postNode.label;

    if (postNode.isLeaf === false) {
      // 非叶节点（岗位下还有用户）
      if (userId) {
        userInfo.userId = +userId;
      }
    } else {
      // 叶节点
      userInfo.userId = +userId;
    }

    // ---- 最终处理 ----
    if (userInfo.userId) {
      this.chooseUser(
        this.userMap[
          this.buildUserMapKey(userInfo.postId, userInfo.userId, sectionId)
        ],
        userInfo.departmentId,
      );
      if (userInfo.departmentId && +userInfo.departmentId > 0) {
        // TODO: 此处会导致用户数据的重新创建
        this.userInfoChange.emit(this.userInfo);
      } else {
        setTimeout(() => {
          this.cascaderValue = this.buildCascaderValueFromUser(
            this.currentUser,
          );
          this.userInfoChange.emit(this.userInfo);
        }, 100);
      }
    } else {
      this.userInfo = userInfo;
    }
  }
}
